import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { PunchupConfirmDialog } from 'features/common/components/Punchup/composites/dialogs/PunchupConfirmDialog';

interface NoPoolConfirmDialogProps {
	/** Return to the form so the manager can pick a pool. */
	onBack: () => void;
	/** Proceed with the save, leaving the new tier un-pooled. */
	onContinue: () => void;
}

/**
 * Shown on save of a BRAND-NEW ticket tier when the show has pools but the tier joins none — the moment to
 * make the one-way door explicit (pool membership locks once a tier starts selling). Shared by both
 * ticket-create surfaces — the accordion `TicketTypeForm` and the `PunchupTicketDetails` sub-page — so the
 * warning copy stays in sync. The pools explainer is the dialog's `secondaryText`, collapsed behind
 * `PunchupDetailText`'s "More info" toggle so the dialog opens on the choice rather than on a wall of
 * teaching copy. See docs/pools_business_logic.md T-5.
 */
export const NoPoolConfirmDialog: FC<NoPoolConfirmDialogProps> = ({ onBack, onContinue }) => {
	const { t } = useTranslation();
	return (
		<PunchupConfirmDialog
			title={t('ticketType-no-pool-title', 'No pool selected')}
			text={t(
				'ticketType-no-pool-text',
				"This show uses ticket pools, but this tier isn't in one. Pool membership locks once the tier starts selling — add it to a pool now, or continue without one."
			)}
			secondaryText={t(
				'ticketType-no-pool-secondary',
				'A pool is one shared capacity that several tiers sell from; a tier in no pool keeps its own separate count instead. Say VIP and General Admission share a 100-ticket pool: every sale from either draws down the same 100, each tier is still capped by its own quantity, and a tier outside the pool sells its own count on top.'
			)}
			collapsibleSecondary
			buttons={[
				{ label: t('ticketType-no-pool-back', 'Select pool'), variant: 'secondary', onClick: onBack },
				{ label: t('ticketType-no-pool-continue', 'Continue'), variant: 'primary', onClick: onContinue },
			]}
		/>
	);
};
